import {MAX_RETRIES} from '../lib/offlineQueue.js';
import type {LocalDraftStamp, QueuedSubmission} from '../lib/offlineQueue.js';
import {queuedReportActor} from '../lib/queuedReportApi.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {
  HUB_DRAFT_KIND,
  assertHubDraftActor,
  hubCarrierRemovalOf,
  hubDraftCsidFor,
  hubDraftHeldDurably,
  newHubDraftInstanceId,
  writeHubDraftCarrierRow,
} from './webformHubRecovery.js';
import type {HubDraft, HubDraftCarrierMeta} from './webformHubRecovery.js';

// The Hub's teardown carrier. The App unmounts the Hub when the signed-in
// account changes, which ends the editing session's autosave mid-flight: the
// last ACKed revision (R1) is durable, but newer pending work (R2) lived only
// in the unmounted component. At teardown the Hub hands its last OWNED
// snapshot here. This module outlives the mount, so the snapshot — its
// answers and the original File bytes — stays bound to the account that
// typed it while one durable write is attempted under that same account.
//
// Custody rules:
// - The owner is the one captured when the work was typed. Nothing here
//   reads the live account to choose an owner; an unknown owner is never
//   written or offered (fail closed, unassigned).
// - The durable row is review-only local custody at a FRESH instance key:
//   no submission id, no send, no automatic retry, no clear, and the source
//   session's row (R1) is never written or removed.
// - A completion updates only its own entry. It never touches a mounted
//   session, its stamp or its status, so newer in-mount work is never
//   marked saved or replaced by an older carrier.
// - A write that fails keeps the snapshot in memory, offered to its owner
//   only, labelled truthfully as NOT saved.
// - Only the verified owner can open or discard an entry, through the Hub's
//   own Open and Discard.

export type HubCarrierCapture = {
  owner: string | null;
  snapshot: HubDraft;
  edits: number;
  /** The snapshot is the owner's EMPTIED form over saved work whose owned
   * clear had not settled: it is carried as a removal tombstone. */
  removal: boolean;
  /** The editing session the work belongs to — the live object, read only
   * after `pending` settles, so its stamp is the newest revision it ACKed. */
  session: {csid: string; stamp: LocalDraftStamp | null} | null;
  /** The torn-down mount's in-flight save/clear, joined before any read. */
  pending: ReadonlyArray<Promise<unknown>>;
};

export type HubCarrierEntry = {
  csid: string;
  owner: string | null;
  snapshot: HubDraft;
  removal: boolean;
  meta: HubDraftCarrierMeta;
  state: 'writing' | 'failed' | 'unassigned';
  message: string;
  createdAt: number;
  settled: Promise<void>;
};

export type ActiveHubSession = {
  owner: string;
  csid: string;
  stamp: LocalDraftStamp | null;
  snapshot?: HubDraft;
  hasUnsettledWork?: boolean;
};

let activeHubSession: ActiveHubSession | null = null;

export function recordActiveHubSession(session: ActiveHubSession | null): void {
  activeHubSession = session;
}

export function getActiveHubSession(): ActiveHubSession | null {
  return activeHubSession;
}

export function clearActiveHubSession(): void {
  activeHubSession = null;
}

const entries = new Map<string, HubCarrierEntry>();
const listeners = new Set<() => void>();

const OPEN_JOIN_MS = 10_000;
const CARRIER_WRITING_MESSAGE = 'Keeping these changes on this device. Nothing is sent automatically.';
const OWNER_REQUIRED_MESSAGE = 'Sign in with the account that typed this draft. Its answers and photos were retained.';

function notify() {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A subscriber's failure never affects custody.
    }
  }
}

function failedMessage(detail: string): string {
  return `NOT saved on this device — kept only while this tab stays open. Open to continue. (${detail})`;
}

async function persist(entry: HubCarrierEntry, owner: string, capture: HubCarrierCapture): Promise<void> {
  const current = () => entries.get(entry.csid) === entry && entry.state === 'writing';
  try {
    // The torn-down mount's own in-flight work settles first; only its own
    // result says what is durable. A first save can commit before teardown
    // while its ACK (the post-save account read) is still delayed, and the
    // session's stamp advances only when that ACK resolves. So there is NO
    // deadline here: a slow ACK is unknown, never proof that nothing is
    // durable, and the entry stays 'writing' (held, listed to its owner,
    // refused by Open until it settles) for as long as it takes.
    await Promise.allSettled(capture.pending);
    const session = capture.session;
    const stamp = session?.stamp ?? null;
    entry.meta = {
      ...entry.meta,
      sourceCsid: session?.csid ?? null,
      sourceStamp: stamp,
      removal: capture.removal && session && stamp ? {csid: session.csid, stamp} : null,
    };
    if (capture.removal && !entry.meta.removal) {
      // Emptied, and nothing of it is durable (never saved, or its clear
      // already settled): there is nothing left to remove or resurrect.
      if (current()) entries.delete(entry.csid);
      return;
    }
    if (!capture.removal && session && (await hubDraftHeldDurably(session.csid, owner, entry.snapshot))) {
      // The session already made exactly this snapshot durable.
      if (current()) entries.delete(entry.csid);
      return;
    }
    const result = await writeHubDraftCarrierRow(entry.snapshot, {
      owner,
      csid: entry.csid,
      meta: entry.meta,
      stillCurrent: current,
    });
    if (!current()) return;
    if (result.status === 'written') {
      // The durable row is now the copy: it lists through the ordinary
      // owner-filtered drafts list, so the memory entry retires.
      entries.delete(entry.csid);
      return;
    }
    entry.state = 'failed';
    entry.message = failedMessage(
      result.status === 'conflict' ? 'A saved draft already uses this key; nothing was overwritten.' : 'Not saved.',
    );
  } catch (error) {
    if (!current()) return;
    entry.state = 'failed';
    entry.message = failedMessage(error instanceof Error ? error.message : 'Could not save this draft locally.');
  } finally {
    notify();
  }
}

/** Teardown hand-off: keep the owned snapshot and attempt its one durable
 * write under its own owner. Returns the carrier key. */
export function carryHubDraft(capture: HubCarrierCapture): string {
  const csid = hubDraftCsidFor(newHubDraftInstanceId());
  const entry: HubCarrierEntry = {
    csid,
    owner: capture.owner,
    snapshot: capture.snapshot,
    removal: capture.removal,
    meta: {
      sourceCsid: capture.session?.csid ?? null,
      sourceStamp: capture.session?.stamp ?? null,
      edits: capture.edits,
      capturedAt: new Date().toISOString(),
      reason: 'teardown',
      removal: null,
    },
    state: capture.owner ? 'writing' : 'unassigned',
    message: capture.owner ? CARRIER_WRITING_MESSAGE : failedMessage('The signed-in account was unresolved.'),
    createdAt: Date.now(),
    settled: Promise.resolve(),
  };
  entries.set(csid, entry);
  if (capture.owner) entry.settled = persist(entry, capture.owner, capture);
  notify();
  return csid;
}

function rowOf(entry: HubCarrierEntry): QueuedSubmission {
  return {
    csid: entry.csid,
    form_kind: HUB_DRAFT_KIND,
    payload: {carrier: entry.meta},
    record: {local_draft: true, hub_carrier_ram: entry.state, hub_carrier_removal: entry.removal},
    status: 'failed',
    retry_count: MAX_RETRIES,
    last_error: entry.message,
    created_at: entry.createdAt,
    last_attempt_at: null,
    actor_profile_id: entry.owner ?? undefined,
  };
}

/** The verified live account's OWN memory-held entries, as drafts-list rows.
 * Another account's entries and unassigned ones are never listed. */
export async function listHubCarrierRows(sb: SupabaseClient): Promise<QueuedSubmission[]> {
  const actor = await queuedReportActor(sb);
  if (!actor) return [];
  const rows = [...entries.values()]
    .filter((entry) => entry.owner === actor && entry.state !== 'unassigned')
    .map(rowOf);
  await assertHubDraftActor(sb, actor);
  return rows;
}

export function isHubCarrierMemoryRow(row: QueuedSubmission): boolean {
  if (!row) return false;
  const record = row.record as {hub_carrier_ram?: unknown} | null;
  return row.form_kind === HUB_DRAFT_KIND && !!record && typeof record === 'object' && !!record.hub_carrier_ram;
}

export function isHubCarrierRow(row: QueuedSubmission): boolean {
  const payload = row.payload as {carrier?: unknown} | null;
  return row.form_kind === HUB_DRAFT_KIND && !!payload && typeof payload === 'object' && !!payload.carrier;
}

function stampKey(csid: string, stamp: unknown): string | null {
  if (!stamp || typeof stamp !== 'object') return null;
  const {mountId, revision} = stamp as {mountId?: unknown; revision?: unknown};
  return typeof mountId === 'string' && typeof revision === 'number' ? `${csid}|${mountId}|${revision}` : null;
}

/** The drafts-list label of a Hub draft row, given every listed row. A
 * carrier says what it carries; a saved draft that a listed tombstone
 * removes at its CURRENT revision says so, and is never offered as the
 * newest work. */
export function describeHubDraftRow(row: QueuedSubmission, rows: ReadonlyArray<QueuedSubmission>): string {
  if (isHubCarrierMemoryRow(row)) {
    const record = row.record as {hub_carrier_ram?: unknown; hub_carrier_removal?: unknown};
    const what = record.hub_carrier_removal ? 'Your removal of a saved Hub draft' : 'Newest Hub changes';
    return record.hub_carrier_ram === 'writing'
      ? `${what} — being kept on this device (review only)`
      : `${what} — NOT saved on this device; kept only while this tab stays open (review only)`;
  }
  if (isHubCarrierRow(row)) {
    return hubCarrierRemovalOf(row.payload)
      ? 'Your removal of a saved Hub draft, kept when the page closed — Open to finish removing it (review only)'
      : 'Newest Hub changes, kept when the page closed — all answers and photos (review only)';
  }
  const removed = new Set<string>();
  for (const other of rows) {
    const removal = isHubCarrierRow(other) ? hubCarrierRemovalOf(other.payload) : null;
    const key = removal && stampKey(removal.csid, removal.stamp);
    if (key) removed.add(key);
  }
  const own = stampKey(row.csid, (row.record as {stamp?: unknown} | null)?.stamp);
  return own && removed.has(own)
    ? 'Saved Hub draft — superseded: you removed these answers before the page closed (review only)'
    : 'Saved Hub draft — all answers, extra groups and photos (review only)';
}

/** Join the entry's in-flight work, BOUNDED so an Open never hangs:
 * 'memory' — settled and still held only in memory (its write failed);
 * 'gone' — settled and durable (open it from storage) or nothing to keep;
 * 'pending' — still waiting on the torn-down mount's own result: unknown,
 *   so the caller refuses truthfully and changes nothing. */
export async function settleHubCarrier(csid: string, boundMs = OPEN_JOIN_MS): Promise<'memory' | 'gone' | 'pending'> {
  const entry = entries.get(csid);
  if (!entry) return 'gone';
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settled = await new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), boundMs);
    void entry.settled.then(() => resolve(true));
  });
  clearTimeout(timer);
  if (!settled) return 'pending';
  return entries.get(csid) === entry ? 'memory' : 'gone';
}

export const HUB_CARRIER_PENDING_MESSAGE =
  'These changes are still being kept on this device. Try again in a moment; nothing was changed.';

async function ownedEntry(sb: SupabaseClient, csid: string): Promise<HubCarrierEntry> {
  const entry = entries.get(csid);
  const actor = await queuedReportActor(sb);
  if (!entry || !actor || entry.owner !== actor || entry.state !== 'failed') throw new Error(OWNER_REQUIRED_MESSAGE);
  await assertHubDraftActor(sb, actor);
  if (entries.get(csid) !== entry) throw new Error(OWNER_REQUIRED_MESSAGE);
  return entry;
}

/** The owner's memory-held snapshot, for Open. The entry stays registered
 * until the Open commits (releaseHubCarrierEntry). */
export async function readHubCarrierSnapshot(sb: SupabaseClient, csid: string): Promise<HubCarrierEntry> {
  return ownedEntry(sb, csid);
}

/** Custody moves into the mounted form: exactly this entry retires. */
export function releaseHubCarrierEntry(entry: HubCarrierEntry): void {
  if (entries.get(entry.csid) === entry) {
    entries.delete(entry.csid);
    notify();
  }
}

/** Cancel of an Open from memory: the same entry is offered again. */
export function restoreHubCarrierEntry(entry: HubCarrierEntry): void {
  if (entries.has(entry.csid)) return;
  entries.set(entry.csid, entry);
  notify();
}

/** The verified owner's discard of exactly one memory-held entry. */
export async function discardHubCarrierEntry(sb: SupabaseClient, csid: string): Promise<void> {
  const entry = await ownedEntry(sb, csid);
  entries.delete(entry.csid);
  notify();
}

export function subscribeHubCarrier(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function _resetHubCarrierForTests(): void {
  entries.clear();
  listeners.clear();
  activeHubSession = null;
}

export function _hubCarrierEntriesForTests(): HubCarrierEntry[] {
  return [...entries.values()];
}
